/**
 * Interview "personas" for the public Try-it-live demo (/hirewithus/demo).
 *
 * The demo entry is a card gallery: each persona is one interviewer/role you
 * can "Try Interview". Q1 of every set is a warm-up / introduction and is
 * excluded from scoring by the backend.
 *
 * `gender` drives the TTS voice (male interviewer → male voice, and vice versa).
 *
 * The RESULT is never canned — after the interview the real transcript is
 * sent to the Web-Interviewer backend (/api/demo/interview/score) and scored
 * by the same Gemini pipeline used in production, so the % is genuine.
 */

// Our own category tabs
export const CATEGORIES = ["All", "Technical", "Product & Strategy", "Sales"];

// Total interview time limit (seconds)
export const INTERVIEW_TIME_LIMIT = 3 * 60; // 3 minutes

// Short acknowledgements the AI interviewer says between questions
export const AI_ACKS = [
  "Got it, thank you.",
  "That's helpful, thanks for sharing.",
  "Understood. Let's move on.",
  "Great, appreciate the detail.",
  "Thanks, that gives me a good sense of it.",
];

export const PERSONAS = [
  {
    id: "ai-engineer",
    name: "Kabir",
    gender: "male",
    role: "AI Engineer",
    category: "Technical",
    tags: ["Python", "Machine Learning"],
    isNew: true,
    questions: [
      "Hi, thanks for joining! To start, could you introduce yourself and tell me about the AI or ML projects you've worked on?",
      "Can you explain the difference between overfitting and underfitting, and how you'd detect and address each?",
      "Walk me through how you'd take a model from a notebook prototype to something running reliably in production.",
      "Tell me about a time a model performed well offline but poorly in the real world. What went wrong?",
      "Finally, how do you keep up with the fast pace of the AI field, and what interests you about this role?",
    ],
  },
  {
    id: "backend-engineer",
    name: "Ishaan",
    gender: "male",
    role: "Backend Engineer",
    category: "Technical",
    tags: ["Node.js", "Databases"],
    isNew: true,
    questions: [
      "Hi! Let's begin — could you introduce yourself and describe the backend systems you've built?",
      "How would you design an API endpoint that needs to stay fast under heavy read traffic?",
      "Explain how you'd debug a service that intermittently returns 500 errors in production.",
      "When would you choose a message queue over a direct synchronous call between services?",
      "Lastly, tell me about a technical decision you're proud of and why it mattered.",
    ],
  },
  {
    id: "frontend-developer",
    name: "Dev",
    gender: "male",
    role: "Frontend Developer",
    category: "Technical",
    tags: ["React", "JavaScript"],
    questions: [
      "Hi, thanks for joining. Could you briefly introduce yourself and your experience with frontend development?",
      "Can you explain the difference between state and props in React, and when you'd reach for a global state manager?",
      "Tell me about a challenging UI bug you fixed. How did you track down the root cause?",
      "How do you approach making a web app fast and accessible for all users?",
      "Finally, why are you interested in this role, and what kind of team do you do your best work in?",
    ],
  },
  {
    id: "data-scientist",
    name: "Meher",
    gender: "female",
    role: "Data Scientist",
    category: "Technical",
    tags: ["Statistics", "Experimentation"],
    questions: [
      "Hi! Let's get started — could you introduce yourself and the kind of data problems you've solved?",
      "How would you design an experiment to test whether a new feature actually improves retention?",
      "Explain the difference between correlation and causation with a real example from your work.",
      "A stakeholder wants a prediction model but you only have two weeks. How do you scope it?",
      "Finally, how do you communicate a complex analysis to a non-technical audience?",
    ],
  },
  {
    id: "product-manager",
    name: "Tara",
    gender: "female",
    role: "Product Manager",
    category: "Product & Strategy",
    tags: ["Roadmapping", "Metrics"],
    isNew: true,
    questions: [
      "Hi, welcome! Could you introduce yourself and tell me about a product you've shipped end to end?",
      "Imagine daily active users dropped 15% last week. Walk me through how you'd investigate.",
      "How do you decide what makes it onto the roadmap when every team wants something different?",
      "Tell me about a feature you killed or rolled back. What did the data tell you?",
      "To wrap up, how do you know a product decision was actually the right one?",
    ],
  },
  {
    id: "vc-associate",
    name: "Arjun",
    gender: "male",
    role: "VC Associate",
    category: "Product & Strategy",
    tags: ["Due Diligence", "Valuation"],
    questions: [
      "Hi there, glad you could make it. Could you introduce yourself and your background in investing or finance?",
      "A seed-stage SaaS startup is growing 20% month over month but burning fast. How do you evaluate it?",
      "What signals tell you a founding team is exceptional versus merely good?",
      "Walk me through how you'd size the market for a product you've never seen before.",
      "Lastly, what's a sector you're personally excited about right now, and why?",
    ],
  },
  {
    id: "sales-executive",
    name: "Naina",
    gender: "female",
    role: "Sales Executive",
    category: "Sales",
    tags: ["Negotiation", "Closing"],
    isNew: true,
    questions: [
      "Hi, glad you're here! Could you introduce yourself and tell me about your sales background?",
      "Let's roleplay: I'm a busy prospect who just picked up a cold call. Open the conversation.",
      "I've said your product is too expensive. How do you respond?",
      "Tell me about a deal you lost and what you changed afterwards.",
      "Lastly, what motivates you day to day in a sales role?",
    ],
  },
  {
    id: "sdr",
    name: "Rohan",
    gender: "male",
    role: "Sales Development Rep",
    category: "Sales",
    tags: ["Prospecting", "Outreach"],
    isNew: true,
    questions: [
      "Hi! Could you introduce yourself and describe your experience with outbound prospecting?",
      "Roleplay time: pitch our product in 30 seconds as if you've just reached a decision-maker.",
      "How do you research an account before reaching out, and what makes a message worth replying to?",
      "A prospect ignored your first three emails. What's your next move?",
      "To finish, how do you stay resilient when most outreach gets no response?",
    ],
  },
];
